import { config } from '../config';

export interface GenerateDescriptionParams {
  crateTitle: string;
  enforceSafety?: boolean;
}

export class DescriptionGenerationService {
  private static readonly STATIC_SUFFIX =
    'In "mixable order" using related BPM + Key, trending charts and hackathon data. Search + stream below, or match your own mp3\'s in our Crate Hackers desktop app.';

  private static readonly SAFETY_PROMPT_PREFIX =
    'Generate a 2-3 sentence description for a music playlist/crate. Focus on the mood, genre, and vibe suggested by the title. Keep it engaging and suitable for all audiences. Do not include any explicit, controversial, or inappropriate content.';

  private static get openAiKey(): string {
    return config.IMAGE_GENERATION_OPENAI_KEY?.trim() ?? '';
  }

  private static get openAiModel(): string {
    return config.IMAGE_GENERATION_OPENAI_MODEL?.trim() ?? 'gpt-3.5-turbo';
  }

  static isConfigured(): boolean {
    const configured = Boolean(this.openAiKey);
    return configured;
  }

  static async generateDescription(
    params: GenerateDescriptionParams,
  ): Promise<string> {
    if (!params.crateTitle?.trim()) {
      throw new Error('Crate title is required to generate a description.');
    }

    if (!this.isConfigured()) {
      throw new Error('AI description generation is not configured.');
    }

    const enrichedParams = this.applySafetyPrompts(params);
    try {
      const generatedText = await this.generateViaOpenAi(enrichedParams);
      const description = `${generatedText.trim()}\n\n${this.STATIC_SUFFIX}`;
      return description;
    } catch (error) {
      console.error('[DescriptionService] Generation error:', error);
      throw new Error(
        error instanceof Error
          ? error.message
          : 'Unable to generate description. Please try again.',
      );
    }
  }

  private static applySafetyPrompts(
    params: GenerateDescriptionParams,
  ): GenerateDescriptionParams {
    if (params.enforceSafety === false) {
      return params;
    }

    return {
      ...params,
      enforceSafety: true,
    };
  }

  private static async generateViaOpenAi(
    params: GenerateDescriptionParams,
  ): Promise<string> {
    const { crateTitle } = params;

    const systemPrompt = this.SAFETY_PROMPT_PREFIX;
    const userPrompt = `Create a description for a music playlist titled: "${crateTitle}"`;
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.openAiKey}`,
      },
      body: JSON.stringify({
        model: this.openAiModel,
        messages: [
          {
            role: 'system',
            content: systemPrompt,
          },
          {
            role: 'user',
            content: userPrompt,
          },
        ],
        temperature: 0.7,
        max_tokens: 150,
      }),
    });

    if (!response.ok) {
      const message = await this.safeParseError(response);
      throw new Error(message || 'OpenAI description generation failed.');
    }

    const payload = await response.json();
    const generatedText = payload.choices?.[0]?.message?.content?.trim() || '';

    if (!generatedText) {
      throw new Error('OpenAI response did not include generated text.');
    }

    return generatedText;
  }

  private static async safeParseError(response: Response): Promise<string> {
    try {
      const data = await response.json();
      if (data?.error) {
        return typeof data.error === 'string' ? data.error : data.error.message;
      }
      if (data?.message) {
        return data.message;
      }
    } catch (err) {
      console.error(
        '[DescriptionService] Failed to parse error response:',
        err,
      );
    }

    try {
      const text = await response.text();
      return text;
    } catch (err) {
      console.error('[DescriptionService] Failed to read error text:', err);
    }
    return 'Failed to fetch description from OpenAI';
  }
}
